/**
 * StatusTransitionModal — Otorgar / Revocar UI para adelantos.
 *
 * Per FWA-PWA-031 G0 D4. Reusable modal para state transitions con required fields.
 */
import { useState } from 'react';
import { Button } from './ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from './ui/Card.js';
import { Input } from './ui/Input.js';

interface OtorgarProps {
  mode: 'otorgar';
  onConfirm: (payload: { estatus: 'otorgado'; fecha_otorgamiento: string }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

interface RevocarProps {
  mode: 'revocar';
  onConfirm: (payload: {
    estatus: 'revocado';
    fecha_revocacion: string;
    motivo_revocacion: string;
  }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

type StatusTransitionModalProps = OtorgarProps | RevocarProps;

export function StatusTransitionModal(props: StatusTransitionModalProps) {
  if (props.mode === 'otorgar') return <OtorgarModal {...props} />;
  return <RevocarModal {...props} />;
}

function OtorgarModal({ onConfirm, onCancel, isSubmitting }: OtorgarProps) {
  const [fecha, setFecha] = useState<string>(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string>('');

  return (
    <dialog
      open
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-none w-full h-full"
      aria-labelledby="otorgar-title"
    >
      <Card className="max-w-md mx-4">
        <CardHeader>
          <CardTitle>
            <span id="otorgar-title">Otorgar adelanto</span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-gray-600 mb-4">
            Esta acción mueve el estatus de <strong>borrador</strong> a <strong>otorgado</strong>.
            Es irreversible.
          </p>
          <Input
            label="Fecha de otorgamiento"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            error={error}
          />
          <div className="flex gap-3 mt-4">
            <Button
              variant="primary"
              disabled={isSubmitting}
              onClick={() => {
                if (!fecha) {
                  setError('Fecha requerida');
                  return;
                }
                onConfirm({ estatus: 'otorgado', fecha_otorgamiento: fecha });
              }}
            >
              {isSubmitting ? 'Guardando…' : 'Confirmar otorgamiento'}
            </Button>
            <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          </div>
        </CardBody>
      </Card>
    </dialog>
  );
}

function RevocarModal({ onConfirm, onCancel, isSubmitting }: RevocarProps) {
  const [fecha, setFecha] = useState<string>(new Date().toISOString().slice(0, 10));
  const [motivo, setMotivo] = useState<string>('');
  const [errors, setErrors] = useState<{ fecha?: string; motivo?: string }>({});

  return (
    <dialog
      open
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-none w-full h-full"
      aria-labelledby="revocar-title"
    >
      <Card className="max-w-md mx-4">
        <CardHeader>
          <CardTitle>
            <span id="revocar-title">Revocar adelanto</span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-gray-600 mb-4">
            Esta acción mueve el estatus de <strong>otorgado</strong> a <strong>revocado</strong>.
            Es terminal (no se puede revertir).
          </p>
          <Input
            label="Fecha de revocación"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            error={errors.fecha}
          />
          <label className="flex flex-col gap-1 mt-3">
            <span className="text-sm font-medium text-gray-700">Motivo (requerido, máx 500)</span>
            <p className="text-xs text-amber-700">
              Este texto se almacena en backend. No incluyas nombres ni datos sensibles.
            </p>
            <textarea
              className="px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              maxLength={500}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
            {errors.motivo && <span className="text-xs text-red-600">{errors.motivo}</span>}
          </label>
          <div className="flex gap-3 mt-4">
            <Button
              variant="primary"
              disabled={isSubmitting}
              onClick={() => {
                const newErrors: { fecha?: string; motivo?: string } = {};
                if (!fecha) newErrors.fecha = 'Fecha requerida';
                if (!motivo.trim()) newErrors.motivo = 'Motivo requerido (no vacío)';
                if (Object.keys(newErrors).length > 0) {
                  setErrors(newErrors);
                  return;
                }
                onConfirm({
                  estatus: 'revocado',
                  fecha_revocacion: fecha,
                  motivo_revocacion: motivo.trim(),
                });
              }}
            >
              {isSubmitting ? 'Guardando…' : 'Confirmar revocación'}
            </Button>
            <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          </div>
        </CardBody>
      </Card>
    </dialog>
  );
}
